import { lazy, LocationProvider, Route, Router } from 'preact-iso';
import { useRef } from 'preact/hooks';
import { Toaster } from 'react-hot-toast';
import { SWRConfig } from 'swr';
import { hideModal, useCurrentModal } from '../global-state/session';
import The404Page from '../routes/404';
import AccountPage from '../routes/account';
import DeleteAccountPage from '../routes/delete-account';
import Downloads from '../routes/downloads';
import EpisodeRoute from '../routes/episode';
import FallbackPageShell from '../routes/fallback-page-shell';
import ListeningHistory from '../routes/history';
import Home from '../routes/home';
import Library from '../routes/library';
import ListenNow from '../routes/now';
import PodcastRoute from '../routes/podcast';
import PricingPage from '../routes/pricing';
import Search from '../routes/search';
import SignIn from '../routes/sign-in';
import SignUp from '../routes/sign-up';
import UpdatePassword from '../routes/update-password';
import debugLog from '../utils/debug';
import { exposeOnWindow } from '../utils/helpers';
import { useOpenExternalLinksInAndroidBrowser } from '../utils/open-external-url';
import { GlobalRouteExposer, useHandleCurrentRoute } from '../utils/routing';
import { DeleteAccountDialog } from './delete-account-dialog';
import { FeedbackDialog } from './feedback-form';
import PlayerPanel from './player-panel';
import { SignInOrSignUpModal } from './sign-in-modal/sign-in-modal';
import { WelcomeModal } from './welcome-modal';

// Lazy-loaded non-essential routes
const AttributionPage = lazy(() => import('../routes/attribution'));
const Privacy = lazy(() => import('../routes/privacy-policy'));
const Terms = lazy(() => import('../routes/terms-of-use'));

const log = (...args: any[]) =>
  debugLog({ logName: 'router', logColor: 'deeppink' }, ...args);

const defaultSwrOptions = {
  // Don't re-validate data that is less than 20 seconds old
  dedupingInterval: 20 * 1000,
};

export function App() {
  // Just providers
  return (
    <LocationProvider>
      <GlobalRouteExposer />
      <SWRConfig value={useRef(defaultSwrOptions).current}>
        <AppContent />
      </SWRConfig>
    </LocationProvider>
  );
}

function AppContent() {
  const currentModal = useCurrentModal();
  const { isInApp, currentRoute } = useHandleCurrentRoute();
  useOpenExternalLinksInAndroidBrowser();

  return (
    // Note: <div id="app"> wraps this entire app (see index.html)
    <>
      <Toaster
        position="top-right"
        containerStyle={{
          top: 'max(16px, var(--safe-area-top))',
        }}
      />
      <div
        id="page-scroll-container"
        class={isInApp ? 'in-app' : ''}
        ref={setPageScrollContainer}
      >
        <Router onRouteChange={handleRouteChange}>
          <Route path="/" component={Home} />
          <Route path="/now" component={ListenNow} />
          <Route path="/library" component={Library} />
          <Route path="/search" component={Search} />
          <Route path="/downloads" component={Downloads} />
          <Route path="/history" component={ListeningHistory} />
          <Route path="/podcast/:id" component={PodcastRoute} />
          <Route path="/podcast/:id/episode/:guidHash" component={EpisodeRoute} />
          <Route path="/sign-in" component={SignIn} />
          <Route path="/sign-up" component={SignUp} />
          <Route path="/update-password" component={UpdatePassword} />
          <Route path="/privacy-policy" component={Privacy} />
          <Route path="/attribution" component={AttributionPage} />
          <Route path="/terms-of-use" component={Terms} />
          <Route path="/fallback-page-shell" component={FallbackPageShell} />
          <Route path="/pricing" component={PricingPage} />
          <Route path="/pricing-in-app" component={PricingPage} />
          <Route path="/account" component={AccountPage} />
          <Route path="/delete-account" component={DeleteAccountPage} />
          <Route default component={The404Page} />
        </Router>
      </div>
      <PlayerPanel isHidden={!isInApp} />
      {currentModal === 'feedback' && <FeedbackDialog onClose={hideModal} />}
      {currentModal === 'sign-in' && <SignInOrSignUpModal onClose={hideModal} />}
      {currentModal === 'delete-account' && <DeleteAccountDialog onClose={hideModal} />}
      {currentModal === 'welcome' && <WelcomeModal onClose={hideModal} />}
    </>
  );
}

function setPageScrollContainer(element: HTMLElement | null) {
  if (element) exposeOnWindow({ pageScrollContainer: element });
}

function handleRouteChange(route: `/${string}`) {
  log('Navigating to', route);
}
